import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Star, RefreshCw, LogOut, Trash2, MessageSquare, ShieldCheck } from "lucide-react";
import { fetchAdminReviews, deleteReview } from "../api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch (e) {
    return "";
  }
};

const Stars = ({ value }) => (
  <span className="inline-flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={13} className={i < value ? "fill-[#c9a24b] text-[#c9a24b]" : "text-[#3a342b]"} />
    ))}
  </span>
);

const AdminReviews = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchAdminReviews();
      setReviews(data);
    } catch (e) {
      // errors surface globally
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const remove = async (id) => {
    const prev = reviews;
    setReviews((r) => r.filter((x) => x.id !== id));
    try {
      await deleteReview(id);
      toast.success("Review deleted");
    } catch (e) {
      setReviews(prev);
      toast.error("Could not delete review");
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-6 py-16" data-testid="admin-reviews">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-[#c9a24b] text-sm tracking-[0.3em] uppercase mb-2">Admin</p>
          <h1 className="font-display text-4xl text-[#f4efe4]">Reviews</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} className="btn-outline-gold px-4 py-2 rounded-full text-xs tracking-[0.15em] uppercase inline-flex items-center gap-2" data-testid="reviews-refresh-btn">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={doLogout} className="text-[#a29a8c] hover:text-[#c9a24b] transition-colors inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em]">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      <div className="flex gap-4 mb-10 text-xs tracking-[0.15em] uppercase">
        <Link to="/admin/orders" className="text-[#a29a8c] hover:text-[#c9a24b] pb-1">Orders</Link>
        <Link to="/admin/inventory" className="text-[#a29a8c] hover:text-[#c9a24b] pb-1">Inventory</Link>
        <Link to="/admin/reviews" className="text-[#c9a24b] border-b border-[#c9a24b] pb-1">Reviews</Link>
        <Link to="/admin/visitors" className="text-[#a29a8c] hover:text-[#c9a24b] pb-1">Visitors</Link>
      </div>

      {reviews.length === 0 ? (
        <div className="bg-[#141210] border border-[#2c2822] rounded-lg px-6 py-16 text-center text-[#a29a8c]">
          <MessageSquare size={28} className="mx-auto text-[#c9a24b] mb-4" />
          No reviews yet.
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((r) => (
            <div key={r.id} className="bg-[#141210] border border-[#2c2822] rounded-xl p-6 flex gap-5" data-testid={`admin-review-${r.id}`}>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <Stars value={r.rating} />
                  <span className="text-[#f4efe4] text-sm font-medium">{r.name}</span>
                  {r.verified && (
                    <span className="inline-flex items-center gap-1 text-[#c9a24b] text-[11px] tracking-[0.05em] uppercase">
                      <ShieldCheck size={12} /> Verified
                    </span>
                  )}
                  <span className="text-[#6b6459] text-xs">· {r.product}</span>
                  <span className="text-[#6b6459] text-xs">· {fmtDate(r.createdAt)}</span>
                  {r.email && <span className="text-[#6b6459] text-xs">· {r.email}</span>}
                </div>
                <p className="text-[#c8c0b2] leading-relaxed">{r.comment}</p>
              </div>
              <button
                onClick={() => remove(r.id)}
                data-testid={`delete-review-${r.id}`}
                className="self-start text-[#a29a8c] hover:text-red-400 transition-colors inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em]"
              >
                <Trash2 size={15} /> Delete
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="text-[#6b6459] text-xs mt-6">Private page — bookmark: /admin/reviews</p>
    </div>
  );
};

export default AdminReviews;
